import { useNavigate, useSearchParams } from "react-router-dom";
import useRoles from "../../features/Roles/hooks/useRoles";
import { useEffect, useState } from "react";
import {
  Button,
  Center,
  Flex,
  Heading,
  Box,
  Text,
  Spinner,
  TableContainer,
  Table as ChakraTable,
  Thead,
  Tr,
  Th,
  Tbody,
  Td,
  Badge,
  Input,
  Select
} from "@chakra-ui/react";
import { FaPlus } from "react-icons/fa";
import { ROUTES } from "../../consts/routes";
import { OlaggModule, PermissionEnum, PurchaseRewardPromotion } from "@olagg/db-types";
import Pagination from "@olagg/ui-kit/Pagination";
import usePurchaseRewardPromotions from "./hooks/usePurchaseRewardPromotions";
import moment from "moment";

const getScopeLabel = (scope: string) => {
  switch (scope) {
    case 'all_products': return 'Todos los productos';
    case 'category': return 'Categoría';
    case 'specific_products': return 'Productos específicos';
    default: return scope;
  }
};

const getScopeColor = (scope: string) => {
  switch (scope) {
    case 'all_products': return 'green';
    case 'category': return 'blue';
    case 'specific_products': return 'purple';
    default: return 'gray';
  }
};

const Actions = ({ promotion, onDeleted }: { promotion: PurchaseRewardPromotion; onDeleted: () => void }) => {
  const { missingPermission } = useRoles();
  const { deletePromotion } = usePurchaseRewardPromotions();
  const navigate = useNavigate();
  const [isDeleting, setIsDeleting] = useState(false);

  const handleView = () => navigate(`/purchase-reward-promotions/${promotion.uid}`);

  const handleDelete = async () => {
    if (window.confirm('¿Estás seguro que deseas eliminar esta promoción?')) {
      setIsDeleting(true);
      try {
        await deletePromotion(promotion.uid);
        await onDeleted();
      } catch (error) {
        console.error("Error eliminando promoción:", error);
      } finally {
        setIsDeleting(false);
      }
    }
  }

  const canEdit = !missingPermission(OlaggModule.PurchaseRewardPromotion, PermissionEnum.EDIT);
  const canDelete = !missingPermission(OlaggModule.PurchaseRewardPromotion, PermissionEnum.DELETE);

  return (
    <Box display="flex" gap="10px" flexWrap="wrap">
      <Button
        isDisabled={!canEdit}
        colorScheme="green"
        size="sm"
        onClick={handleView}
      >
        Ver / Editar
      </Button>
      <Button
        isDisabled={!canDelete || isDeleting}
        colorScheme="red"
        size="sm"
        onClick={handleDelete}
        isLoading={isDeleting}
        loadingText="Eliminando..."
      >
        Eliminar
      </Button>
    </Box>
  )
}

const PurchaseRewardPromotionsList = () => {
  const navigate = useNavigate();
  const { missingPermission } = useRoles();
  const [searchParams, setSearchParams] = useSearchParams();
  const { getPromotions, loading } = usePurchaseRewardPromotions();
  const [promotions, setPromotions] = useState<PurchaseRewardPromotion[]>([]);
  const [count, setCount] = useState(0);
  const [debouncedName, setDebouncedName] = useState('');
  const [refreshTrigger, setRefreshTrigger] = useState(0);
  const [filter, setFilter] = useState({
    name: searchParams.get('name') || '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 10),
    active_only: searchParams.get('active_only') === 'true'
  });
  const { page, limit, name, active_only } = filter;

  const onFilter = ({
    name: filterName,
    value
  }: { name: string, value: any }) => {
    const parsedValue = (filterName === 'page' || filterName === 'limit') ? Number(value) : value;
    setFilter({ ...filter, [filterName]: parsedValue });

    if (value !== undefined && value !== '' && value !== false) {
      searchParams.set(filterName, String(value));
    } else {
      searchParams.delete(filterName);
    }
    setSearchParams(searchParams);
  };

  const onClearFilters = () => {
    setFilter({
      name: '',
      page: 1,
      limit: 10,
      active_only: false
    });
    setSearchParams(new URLSearchParams());
  };

  const refreshPromotions = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  const fetchPromotions = async () => {
    try {
      const response = await getPromotions({
        limit,
        page,
        name: debouncedName || undefined,
        active_only: active_only || undefined
      });
      setPromotions(response.purchaseRewardPromotions);
      setCount(response.meta.count);
    } catch (error) {
      console.error('Error fetching promotions:', error);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedName(name);
    }, 500);

    return () => clearTimeout(timer);
  }, [name]);

  useEffect(() => {
    fetchPromotions();
  }, [page, limit, debouncedName, active_only, refreshTrigger]);

  useEffect(() => {
    const urlPage = searchParams.get('page');
    const urlLimit = searchParams.get('limit');
    const urlName = searchParams.get('name');
    const urlActiveOnly = searchParams.get('active_only');

    if (urlPage || urlLimit || urlName || urlActiveOnly) {
      setFilter({
        name: urlName || '',
        page: Number(urlPage || 1),
        limit: Number(urlLimit || 10),
        active_only: urlActiveOnly === 'true'
      });
    }
  }, []);

  return (
    <Center w='full' pb={4}>
      <Box color={'white'} w='full'>
        <Heading textAlign="center" mb={6}>
          Promociones de Recompensa por Compra
        </Heading>

        <Flex w='full' alignItems='center' gap='5'>
          <Button
            mt='2' mb='5'
            display='flex'
            variant="outlinedGradient"
            leftIcon={<FaPlus />}
            onClick={() => navigate(ROUTES.PURCHASE_REWARD_PROMOTIONS.create)}
            isDisabled={missingPermission(OlaggModule.PurchaseRewardPromotion, PermissionEnum.CREATE)}
          >
            Agregar Promoción
          </Button>
        </Flex>

        <Box
          mb={6}
          p={4}
          bg="gray.800"
          borderRadius="md"
          border="1px"
          borderColor="gray.600"
          display="grid"
          gridTemplateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }}
          gap={4}
        >
          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Nombre
            </Text>
            <Input
              placeholder="Buscar por nombre..."
              value={name}
              onChange={(e) => onFilter({ name: 'name', value: e.target.value })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            />
          </Box>

          <Box>
            <Text color="white" fontSize="sm" mb={1}>
              Estado
            </Text>
            <Select
              value={active_only ? 'true' : ''}
              onChange={(e) => onFilter({ name: 'active_only', value: e.target.value === 'true' })}
              bg="gray.700"
              borderColor="gray.600"
              color="white"
              size="sm"
            >
              <option value="">Todas</option>
              <option value="true">Solo activas</option>
            </Select>
          </Box>

          <Box>
            <Text color="transparent" fontSize="sm" mb={1}>
              .
            </Text>
            <Button
              colorScheme="gray"
              variant="outline"
              onClick={onClearFilters}
              size="sm"
              w="full"
            >
              Limpiar filtros
            </Button>
          </Box>
        </Box>

        {loading ? (
          <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
            <Spinner />
          </Box>
        ) : (
          promotions?.length > 0 ? (
            <Flex w={'full'} pb={4} justifyContent='center' flexDirection={'column'}>
              <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
                <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="60vh">
                  <ChakraTable
                    variant="striped"
                    size="sm"
                    colorScheme="olaggPurple"
                    fontSize="18px"
                    position='relative'
                    whiteSpace={'normal'}
                  >
                    <Thead>
                      <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
                        <Th py={'20px'} color="white">Nombre</Th>
                        <Th py={'20px'} color="white">Alcance</Th>
                        <Th py={'20px'} color="white">Recompensas</Th>
                        <Th py={'20px'} color="white">Vigencia</Th>
                        <Th py={'20px'} color="white">Activa</Th>
                        <Th py={'20px'} color="white">Claims</Th>
                        <Th py={'20px'} color="white">Acciones</Th>
                      </Tr>
                    </Thead>
                    <Tbody>
                      {promotions?.map((promotion, index) => (
                        <Tr key={index} color="white">
                          <Td py={'20px'}>
                            <Text fontWeight="bold">{promotion.name}</Text>
                            {promotion.description && (
                              <Text fontSize="xs" color="gray.400">{promotion.description}</Text>
                            )}
                          </Td>
                          <Td py={'20px'}>
                            <Badge colorScheme={getScopeColor(promotion.scope)}>
                              {getScopeLabel(promotion.scope)}
                            </Badge>
                            {promotion.scope === 'category' && promotion.category && (
                              <Text fontSize="xs" mt={1}>{promotion.category}</Text>
                            )}
                            {promotion.scope === 'specific_products' && promotion.products && (
                              <Text fontSize="xs" mt={1}>{promotion.products.length} productos</Text>
                            )}
                          </Td>
                          <Td py={'20px'}>
                            <Box fontSize="xs">
                              {(promotion.fixedXp > 0 || promotion.xpPerUsdc > 0) && (
                                <Text>XP: {promotion.fixedXp} + {promotion.xpPerUsdc}/USDC</Text>
                              )}
                              {(promotion.fixedTreasureOpportunities > 0 || promotion.treasurePerUsdc > 0) && (
                                <Text>Tesoros: {promotion.fixedTreasureOpportunities} + {promotion.treasurePerUsdc}/USDC</Text>
                              )}
                              {(promotion.fixedClawCoins > 0 || promotion.clawCoinsPerUsdc > 0) && (
                                <Text>Monedas: {promotion.fixedClawCoins} + {promotion.clawCoinsPerUsdc}/USDC</Text>
                              )}
                            </Box>
                          </Td>
                          <Td py={'20px'}>
                            <Box fontSize="xs">
                              {promotion.startsAt && (
                                <Text>Desde: {moment(promotion.startsAt).format('DD/MM/YYYY')}</Text>
                              )}
                              {promotion.endsAt && (
                                <Text>Hasta: {moment(promotion.endsAt).format('DD/MM/YYYY')}</Text>
                              )}
                              {!promotion.startsAt && !promotion.endsAt && (
                                <Text color="gray.400">Sin límite</Text>
                              )}
                            </Box>
                          </Td>
                          <Td py={'20px'}>
                            <Badge colorScheme={promotion.active ? 'green' : 'red'}>
                              {promotion.active ? 'Activa' : 'Inactiva'}
                            </Badge>
                          </Td>
                          <Td py={'20px'}>{promotion.claimsCount}</Td>
                          <Td py={'20px'}>
                            <Actions promotion={promotion} onDeleted={refreshPromotions} />
                          </Td>
                        </Tr>
                      ))}
                    </Tbody>
                  </ChakraTable>
                </TableContainer>
              </Flex>
            </Flex>
          ) : (
            <Box height='300px' display='flex' justifyContent='center' alignItems='center' fontWeight='600' flexDirection='column'>
              <Text>No hay promociones cargadas</Text>
            </Box>
          )
        )}

        <Pagination
          params={{ limit, page, count }}
          onClick={page =>
            onFilter({
              name: 'page',
              value: String(page + 1)
            })
          }
          onLimitChange={val =>
            onFilter({
              name: 'limit',
              value: val
            })
          }
          hideLimitInput
        />
      </Box>
    </Center>
  )
}

export default PurchaseRewardPromotionsList;
